// Configuration constants
const CONFIG = {
  // TianDiTu API key (apply from TianDiTu official website)
  TD_KEY:
    process.env.NODE_ENV === 'development'
      ? '688b81690c67021557839d2d9f1a2d74'
      : '583d6b78476773a80132df839121be85',

  // Map initial settings
  MAP_CONFIG: {
    style: {
      version: 8,
      sources: {},
      layers: []
    },
    center: [116.4039, 39.9149], // Beijing coordinates
    zoom: 10,
    minZoom: 3,
    maxZoom: 17
  },

  // Animation parameters
  ANIMATION: {
    SPEED_FACTOR: 30, // frames per longitude degree
    LINE_COLOR: '#ed6498',
    LINE_WIDTH: 5,
    LINE_OPACITY: 0.8,
    INITIAL_ZOOM_DURATION: 2000, // 2 seconds for initial zoom
    FLY_TO_DURATION: 3000, // 3 seconds for fly-to animation
    TRAJECTORY_SPEED: 1000 // 1 second per coordinate point
  },

  // Trajectory configuration
  TRAJECTORY: {
    DEFAULT_ROUTE: [
      [116.4039, 39.9149], // Beijing
      [116.4139, 39.9049],
      [116.4239, 39.8949],
      [116.4339, 39.8849]
    ]
  }
};

export class MapApp {
  constructor({ container }) {
    this.container = container;
    this.map = null;
    this.marker = null;
    this.animation = null;
    this.startTime = 0;
    this.progress = 0;
    this.resetTime = false;
    this._htmlMarkers = [];
    this._circleSourceId = 'circle-points';
    this._circleLayerId = 'circle-points-layer';
  }

  // Initialize the application
  init() {
    this.initMap();

    return this;
  }

  // Initialize map
  initMap() {
    this.map = new maplibregl.Map({ ...CONFIG.MAP_CONFIG, container: this.container });

    // Add TianDiTu layers after map loads
    this.map.on('load', () => {
      this.addMapControls();
    });

    return this;
  }

  // Add TianDiTu raster layers
  addTianDiTuLayers({ before }) {
    this.map.on('load', () => {
      // 1. Add vector base map source
      this.map.addSource('tdt-vector', {
        type: 'raster',
        tiles: [
          `https://t0.tianditu.gov.cn/vec_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=vec&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk=${CONFIG.TD_KEY}`
        ],
        tileSize: 256,
        attribution: '© 天地图 GS(2023)1822号 - 甲测资字1100333'
      });

      // 2. Add vector annotation source
      this.map.addSource('tdt-vector-annotation', {
        type: 'raster',
        tiles: [
          `https://t0.tianditu.gov.cn/cva_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=cva&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk=${CONFIG.TD_KEY}`
        ],
        tileSize: 256,
        attribution: '© 天地图 GS(2023)1822号 - 甲测资字1100333'
      });

      // 3. Add vector base layer with low z-index to ensure 3D models render on top
      this.map.addLayer(
        {
          id: 'tdt-vector-layer',
          type: 'raster',
          source: 'tdt-vector',
          minzoom: 3,
          maxzoom: 18,
          paint: {
            'raster-opacity': 0.95
          }
        },
        before
      ); // Add before Three.js scene layer

      // 4. Add vector annotation layer (also with low z-index)
      this.map.addLayer(
        {
          id: 'tdt-vector-annotation-layer',
          type: 'raster',
          source: 'tdt-vector-annotation',
          minzoom: 3,
          maxzoom: 18,
          paint: {
            'raster-opacity': 0.9
          }
        },
        before
      ); // Add before Three.js scene layer
    });

    return this;
  }

  // Add map controls (zoom, rotation)
  addMapControls() {
    this.map.addControl(new maplibregl.NavigationControl(), 'top-right');
  }

  // Add marker to Beijing
  addMarker({
    coord,
    html,
    markerOpts = { color: 'grey' },
    popupOpts = { closeButton: true, closeOnClick: true },
    popupMaxWidth = '300px'
  }) {
    // Create enhanced popup with custom HTML content
    const popup = new maplibregl.Popup(popupOpts).setDOMContent(html).setMaxWidth(popupMaxWidth);

    this.marker = new maplibregl.Marker(markerOpts)
      .setLngLat(coord)
      .setPopup(popup)
      .addTo(this.map);

    return this.marker;
  }

  // Fly to specific coordinate with animation
  flyToCoordinate(lng, lat, zoom = 12) {
    this.map.flyTo({
      center: [lng, lat],
      zoom: zoom,
      duration: CONFIG.ANIMATION.FLY_TO_DURATION,
      animate: true
    });

    // Add a temporary marker at the destination
    this.addTemporaryMarker(lng, lat);
  }

  // Add temporary marker that fades out
  addTemporaryMarker(lng, lat) {
    // Remove existing temporary marker if any
    if (this.tempMarker) {
      this.tempMarker.remove();
    }

    // Create new temporary marker
    this.tempMarker = new maplibregl.Marker({
      color: '#ff6b6b',
      scale: 1.5
    })
      .setLngLat([lng, lat])
      .addTo(this.map);

    // Remove marker after 5 seconds
    setTimeout(() => {
      if (this.tempMarker) {
        this.tempMarker.remove();
        this.tempMarker = null;
      }
    }, 5000);
  }

  // Show trajectory route animation
  showTrajectoryRoute(
    coordinates = CONFIG.TRAJECTORY.DEFAULT_ROUTE,
    speed = CONFIG.ANIMATION.TRAJECTORY_SPEED
  ) {
    // Clear existing route
    this.clearTrajectory();

    // Add route source
    this.map.addSource('trajectory-route', {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: coordinates
        }
      }
    });

    // Add route layer
    this.map.addLayer({
      id: 'trajectory-line',
      type: 'line',
      source: 'trajectory-route',
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#3498db',
        'line-width': 4,
        'line-opacity': 0.8
      }
    });

    // Animate along the route
    this.animateAlongRoute(coordinates, speed);
  }

  // Animate movement along trajectory route
  animateAlongRoute(coordinates, speed) {
    if (!coordinates || coordinates.length < 2) return;

    let currentIndex = 0;

    const moveNext = () => {
      if (currentIndex >= coordinates.length) {
        // Route completed
        this.addCompletionMarker(coordinates[coordinates.length - 1]);
        return;
      }

      const [lng, lat] = coordinates[currentIndex];

      // Move marker to current position
      if (this.trajectoryMarker) {
        this.trajectoryMarker.setLngLat([lng, lat]);
      } else {
        // Create marker for first time
        this.trajectoryMarker = new maplibregl.Marker({
          color: '#e74c3c',
          scale: 1.2
        })
          .setLngLat([lng, lat])
          .addTo(this.map);
      }

      // Fly to current position
      this.map.easeTo({
        center: [lng, lat],
        duration: speed,
        animate: true
      });

      currentIndex++;
      setTimeout(moveNext, speed);
    };

    moveNext();
  }

  // Add completion marker at end of route
  addCompletionMarker(coordinates) {
    const popup = new maplibregl.Popup({ closeButton: true }).setHTML(`
            <div class="popup-title">路线完成</div>
            <div class="popup-content">
             已到达终点: ${coordinates[0].toFixed(4)}°E, ${coordinates[1].toFixed(4)}°N
            </div>
          `);

    new maplibregl.Marker({
      color: '#2ecc71',
      scale: 1.5
    })
      .setLngLat(coordinates)
      .setPopup(popup)
      .addTo(this.map);
  }

  // Clear trajectory route and markers
  clearTrajectory() {
    // Remove layers and sources
    if (this.map.getLayer('trajectory-line')) {
      this.map.removeLayer('trajectory-line');
    }
    if (this.map.getSource('trajectory-route')) {
      this.map.removeSource('trajectory-route');
    }

    // Remove markers
    if (this.trajectoryMarker) {
      this.trajectoryMarker.remove();
      this.trajectoryMarker = null;
    }
  }

  // Set custom trajectory route
  setCustomRoute(coordinates) {
    CONFIG.TRAJECTORY.CUSTOM_ROUTE = coordinates;
  }

  // Get current custom route
  getCustomRoute() {
    return CONFIG.TRAJECTORY.CUSTOM_ROUTE;
  }

  // ==================== RTEAO-Monitor 专用方法 ====================

  /**
   * 飞行到指定坐标（带动画）
   * @param {[number, number]} center 中心坐标 [lng, lat]
   * @param {number} zoom 缩放级别
   * @param {Object} options 动画选项
   */
  flyTo(center, zoom = 12, options = {}) {
    const { duration = 2000, pitch = 0, bearing = 0 } = options;

    this.map.flyTo({
      center,
      zoom,
      pitch,
      bearing,
      duration,
      essential: true
    });
  }

  /**
   * 添加自定义 HTML 标记点
   * @param {Array} data 标记数据数组，每项包含 { coordinate, ...props }
   * @param {Function} createEl 创建 DOM 元素的函数
   * @param {Object} options 选项
   */
  addHtmlMarkers(data, createEl, options = {}) {
    const { offsetX = 0, offsetY = 0 } = options;

    // 清除旧的标记
    this._htmlMarkers.forEach(m => m.remove());
    this._htmlMarkers = [];

    data.forEach(item => {
      const { coordinate, ...props } = item;
      const el = createEl(props);

      if (!el) return;

      // 创建自定义标记
      const marker = new maplibregl.Marker({
        element: el,
        anchor: 'bottom',
        offset: [offsetX, offsetY]
      })
        .setLngLat(coordinate)
        .addTo(this.map);

      this._htmlMarkers.push(marker);
    });
  }

  /**
   * 更新 HTML 标记数据
   * @param {Array} data 新的标记数据
   * @param {Function} createEl 创建 DOM 元素的函数
   * @param {Object} options 选项
   */
  updateHtmlMarkers(data, createEl, options = {}) {
    this.addHtmlMarkers(data, createEl, options);
  }

  /**
   * 添加圆形点图层
   * @param {Array} data 点数据数组，每项包含 { coordinate, name, color, radius }
   */
  addCircleLayer(data) {
    const features = data.map(item => ({
      type: 'Feature',
      properties: {
        name: item.name || '',
        color: item.color || '#3fb1e3',
        radius: item.radius || 8
      },
      geometry: {
        type: 'Point',
        coordinates: item.coordinate
      }
    }));

    const geojson = {
      type: 'FeatureCollection',
      features
    };

    // 如果源已存在，更新数据
    if (this.map.getSource(this._circleSourceId)) {
      this.map.getSource(this._circleSourceId).setData(geojson);
      return;
    }

    // 添加源
    this.map.addSource(this._circleSourceId, {
      type: 'geojson',
      data: geojson
    });

    // 添加圆形图层
    this.map.addLayer({
      id: this._circleLayerId,
      type: 'circle',
      source: this._circleSourceId,
      paint: {
        'circle-radius': ['get', 'radius'],
        'circle-color': ['get', 'color'],
        'circle-opacity': 0.8,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
        'circle-stroke-opacity': 0.6
      }
    });
  }

  /**
   * 更新圆形点图层数据
   * @param {Array} data 新的点数据
   */
  updateCircleLayer(data) {
    this.addCircleLayer(data);
  }

  /**
   * 设置地图样式（暗色主题）
   */
  setDarkTheme() {
    // 使用 SVG 滤镜实现暗色主题
    const container = this.container.closest('.maplibre-gl-container');
    if (container) {
      container.classList.add('dark');
    }
  }

  /**
   * 设置地图样式（亮色主题）
   */
  setLightTheme() {
    const container = this.container.closest('.maplibre-gl-container');
    if (container) {
      container.classList.remove('dark');
    }
  }

  /**
   * 启用滚轮缩放
   */
  enableScrollZoom() {
    this.map.scrollZoom.enable();
  }

  /**
   * 获取地图边界
   * @returns {Object} 边界对象
   */
  getBounds() {
    return this.map.getBounds();
  }

  /**
   * 适应指定坐标的视图
   * @param {Array} coordinates 坐标数组
   * @param {Object} options 选项
   */
  fitCoordinates(coordinates, options = {}) {
    const { padding = 50, maxZoom = 15 } = options;

    if (!coordinates || coordinates.length === 0) return;

    const bounds = new maplibregl.LngLatBounds();
    coordinates.forEach(coord => bounds.extend(coord));

    this.map.fitBounds(bounds, { padding, maxZoom });
  }
}
